/**
 * RisikoprofilTab - the Kunde-Detail «Risikoprofil» tab (P2 Placement Slice 1, Ausschreibung
 * Ch 7.1). A data-quality header + «Als geprueft markieren», then three sections, each behind its
 * own Kachel visibility key:
 *   kunden.risikoprofil.generell     → RisikoprofilGenerellSection
 *   kunden.risikoprofil.basissparten → BasisspartenSection
 *   kunden.risikoprofil.details      → DetailsJePoliceSection
 *
 * Edit controls are gated on `canWrite` (customer_risk.write); the api is the hard line.
 */

import {
  useRisikoprofil,
  useCoverageBaseline,
  useRisikoprofilPatch,
  useRisikoprofilReview,
  useRisikoprofilEinwilligung,
  useCoverageBaselinePut,
  useKundeSchadenverlauf,
  useKundeSchadenSpartePut,
  useKundeSchadenSparteRemove,
  useKundeSchadenLegacyClear,
} from '../../hooks/useRisikoprofil';
import { Button } from '../../../../components/ui/Button';
import { RisikoprofilGenerellSection } from './RisikoprofilGenerellSection';
import { SchadenJeSparteSection } from './SchadenJeSparteSection';
import { BasisspartenSection } from './BasisspartenSection';
import { DetailsJePoliceSection } from './DetailsJePoliceSection';

export function RisikoprofilTab({
  subjectType,
  subjectId,
  canWrite,
  isHidden,
  onOpenPolicy,
}: {
  subjectType: 'person' | 'firma';
  subjectId: string;
  canWrite: boolean;
  /** Bereich-3 Kachel visibility predicate (ancestor-aware). */
  isHidden: (key: string) => boolean;
  onOpenPolicy?: (policyId: string) => void;
}) {
  const profileQuery = useRisikoprofil(subjectType, subjectId);
  const baselineQuery = useCoverageBaseline(subjectType, subjectId);
  const patch = useRisikoprofilPatch(subjectType, subjectId);
  const review = useRisikoprofilReview(subjectType, subjectId);
  const einwilligung = useRisikoprofilEinwilligung(subjectType, subjectId);
  const baselinePut = useCoverageBaselinePut(subjectType, subjectId);
  const claimsQuery = useKundeSchadenverlauf(subjectType, subjectId);
  const claimsPut = useKundeSchadenSpartePut(subjectType, subjectId);
  const claimsRemove = useKundeSchadenSparteRemove(subjectType, subjectId);
  const claimsLegacyClear = useKundeSchadenLegacyClear(subjectType, subjectId);

  if (profileQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Lade Risikoprofil…</p>;
  }
  if (profileQuery.isError || !profileQuery.data) {
    return (
      <p role="alert" className="text-sm text-destructive" data-testid="rp-error">
        Das Risikoprofil konnte nicht geladen werden.
      </p>
    );
  }

  const profile = profileQuery.data;
  const score = profile.data_quality_score;

  return (
    <div className="flex flex-col gap-5" data-testid="risikoprofil-tab">
      {/* Score header + review */}
      <div className="flex items-center justify-between gap-3 rounded-lg border bg-card p-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-muted-foreground">Datenqualitaet</span>
          <span className="text-lg font-semibold text-foreground" data-testid="rp-score">
            {score === null ? '-' : `${score.toFixed(0)} %`}
          </span>
          {profile.last_reviewed_at && (
            <span className="text-xs text-muted-foreground">
              Zuletzt geprueft: {new Date(profile.last_reviewed_at).toLocaleDateString('de-CH')}
            </span>
          )}
        </div>
        {canWrite && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => review.mutate()}
            disabled={review.isPending}
            data-testid="rp-review"
          >
            {review.isPending ? 'Speichern…' : 'Als geprueft markieren'}
          </Button>
        )}
      </div>

      {!isHidden('kunden.risikoprofil.generell') && (
        <RisikoprofilGenerellSection
          profile={profile}
          canWrite={canWrite}
          isSaving={patch.isPending || einwilligung.isPending}
          onPatch={(body) => patch.mutate(body)}
          onEinwilligung={(erfasst) => einwilligung.mutate(erfasst)}
          schadenSlot={
            claimsQuery.data ? (
              <SchadenJeSparteSection
                data={claimsQuery.data}
                canWrite={canWrite}
                isSaving={claimsPut.isPending || claimsRemove.isPending || claimsLegacyClear.isPending}
                onPut={(branchencode, body) => claimsPut.mutate({ branchencode, body })}
                onRemove={(branchencode) => claimsRemove.mutate(branchencode)}
                onClearLegacy={() => claimsLegacyClear.mutate()}
              />
            ) : null
          }
        />
      )}

      {!isHidden('kunden.risikoprofil.basissparten') && baselineQuery.data && (
        <BasisspartenSection
          items={baselineQuery.data.items}
          canWrite={canWrite}
          onPut={(sparteKey, body) => baselinePut.mutate({ sparteKey, body })}
          onOpenPolicy={onOpenPolicy}
        />
      )}

      {!isHidden('kunden.risikoprofil.details') && baselineQuery.data && (
        <DetailsJePoliceSection items={baselineQuery.data.items} onOpenPolicy={onOpenPolicy} />
      )}
    </div>
  );
}
